import React from 'react';
import { faFileCode } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/avoiding-ontology-loss.jpg
import avoidingOntologyLossImg from '../../../assets/images/blogs/Avoiding-Ontology-Loss.png';

const AvoidingOntologyLoss = () => (
    <BlogPost
        title="AI Native Semantics: Avoiding Ontology Loss"
        subtitle="What are you giving up by skipping the formal ontology step? Less than you think."
        category="Cognition"
        categoryIcon={faFileCode}
        color="#003366"
        date="March 2026"
        readTime="5 min read"
        image={avoidingOntologyLossImg}
        imageAlt="Avoiding ontology loss - preserving full linguistic structure"
        ctaHeading="Want semantics without the loss?"
        ctaText="Book a discovery call and we'll show you how AI-native semantics preserves the full meaning that traditional approaches lose."
        seoDescription="Why traditional knowledge engineering loses information through interpretation, and how AI-native semantics preserves full linguistic structure."
        seoKeywords="ontology, knowledge engineering, AI semantics, linguistic structure, knowledge graphs, Nebulyx Cognition"
        slug="avoiding-ontology-loss"
    >
        <p>
            Ontologies can be lossy artefacts. In response to my different approach to
            create an AI-native semantic reasoning knowledge base, a great question was
            asked: what are you giving up by skipping the formal ontology step?
        </p>

        <p>
            <strong>The answer: less than you think.</strong>
        </p>

        <h3>The Problem with Traditional Knowledge Engineering</h3>

        <p>
            It happens at two levels.
        </p>

        <p>
            <strong>First, the ontology - the schema layer.</strong> Experts define classes,
            properties, hierarchies upfront. This can take months and can't anticipate every
            future question (even with rigour e.g. pre-interviewing).
        </p>

        <p>
            <strong>Second, with the knowledge graph - the instance layer.</strong> Engineers
            extract facts and encode them as triples: subject-predicate-object.
        </p>

        <p>
            Both steps can lose information.
        </p>

        <p>
            When a regulation says <em>"shall report transactions where value exceeds €1M,
            except intraday transactions"</em> - a knowledge engineer might capture "must
            report transactions" and separately note "intraday exempt."
        </p>

        <blockquote className="blog-post-quote">
            "But this loses the structure. One is an exception to the other, qualified by
            a condition. Both are interpretations and model transformations based on human
            interpretation, with what is known at the time."
        </blockquote>

        <p>
            Which can result in these failure modes:
        </p>

        <p>
            <strong>Incomplete</strong> - relationships lost in human interpretation<br />
            <strong>Brittle</strong> - document changes require manual re-curation<br />
            <strong>Expensive</strong> - time and effort to assess and hand-craft every update
        </p>

        <h3>The Language Already Encodes the Relationships</h3>

        <p>
            The text captures relationships through syntax and grammar.
        </p>

        <p>
            "Unless" creates exceptions. "Subject to" introduces conditions. "Amended as
            follows" signals modification.
        </p>

        <p>
            These patterns aren't domain-specific - they work the same in regulations,
            clinical protocols, or recipes.
        </p>

        <h3>A Different Approach</h3>

        <p>
            We preserve the full linguistic structure and learn how meaning flows through it.
        </p>

        <p>
            <strong>Sentences retain complete structure</strong> - not reduced to triples.<br />
            <strong>Relationships detected from language itself</strong> - "if", "except",
            "pursuant to" signal how sentences connect.<br />
            <strong>Cross-document links emerge automatically.</strong>
        </p>

        <h3>At Query Time</h3>

        <p>
            Ask about reporting obligations and you get the core requirement plus its
            conditions, exceptions and consequences.
        </p>

        <p>
            When source documents change, relationships update automatically - without heavy
            re-curation process.
        </p>

        <p>
            <strong>The ontology and knowledge graph aren't totally gone. They're
            reimagined</strong> - where deep semantics are discovered and encoded from
            the language itself.
        </p>

        <p>
            It's the difference in agility, depth, and months/years of effort for change. 🎯
        </p>
    </BlogPost>
);

export default AvoidingOntologyLoss;
